'use client'

import { useEffect, useState, useCallback } from 'react'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { useRouter } from 'next/navigation'
import useDebounce from '@/useDebounce'
import { Button } from '@/General/Button'

export const SearchBar = ({
  initialSearchTerm,
}: {
  initialSearchTerm: string
}) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm)
  const debouncedSearchTerm = useDebounce(searchTerm, 500)
  const router = useRouter()

  const updateSearchParam = useCallback(
    (value: string) => {
      const params = new URLSearchParams()
      if (value) params.set('search', value)
      router.replace(`/magic-book-reviews?${params.toString()}`)
    },
    [router]
  )

  useEffect(() => {
    updateSearchParam(debouncedSearchTerm)
  }, [debouncedSearchTerm, updateSearchParam])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    updateSearchParam(searchTerm)
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="my-6 rounded-lg bg-[#f2f2f2] p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-3 md:gap-4"
    >
      <label
        htmlFor="book-review-search"
        className=" shrink-0 font-semibold text-gray-800"
      >
        Search for a review:
      </label>

      <div className="relative flex-1 min-w-0">
        <input
          id="book-review-search"
          type="search"
          value={searchTerm}
          placeholder="Search for a book or author..."
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-white rounded-lg border border-gray-300 py-2.5 pl-4 pr-11 text-gray-900 placeholder:text-gray-500 focus:border-vi-red focus:ring-2 focus:ring-vi-red/20 focus:outline-none transition-colors"
          aria-label="Search book reviews"
        />
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
          <MagnifyingGlassIcon
            aria-hidden="true"
            className="h-5 w-5 text-gray-400"
          />
        </div>
      </div>

      <Button
        type="submit"
        color="red"
        size="md"
        className="shrink-0 w-full sm:w-auto"
      >
        Search
      </Button>
    </form>
  )
}
